import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { getMyProfile, updateMyProfile, UserProfile } from '../api/profile';
import { login } from '../store/authSlice';
import { AppDispatch, RootState } from '../../../store/store';
import { getStoredTokens } from '../../../adapters/api-client/tokenStorage';

const ROLE_LABEL: Record<string, string> = {
  PARENT: 'Padre/Madre',
  STUDENT: 'Estudiante',
  TEACHER: 'Profesor',
  ADMIN: 'Administrador',
  MODERATOR: 'Moderador',
};

/**
 * TCH-10 / S5.7 — the real profile screen.
 *
 * Replaces a two-line placeholder. Reads and writes `/users/me`, whose
 * UpdateProfileDto accepts firstName, lastName and preferences only — email and
 * role are identity, changed through auth/admin flows rather than here, so they
 * are shown read-only rather than as fields that silently fail to save.
 */
const Profile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const authUser = useSelector((state: RootState) => state.auth.user);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await getMyProfile();
        if (cancelled) return;
        setProfile(me);
        setFirstName(me.firstName ?? '');
        setLastName(me.lastName ?? '');
        setTimezone(me.preferences?.timezone ?? '');
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'No pudimos cargar tu perfil.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (!firstName.trim() || !lastName.trim()) {
      setError('El nombre y los apellidos son obligatorios.');
      return;
    }

    setSaving(true);
    try {
      const updated = await updateMyProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        preferences: {
          ...profile?.preferences,
          ...(timezone.trim() ? { timezone: timezone.trim() } : {}),
        },
      });
      setProfile(updated);
      setNotice('Guardamos tus cambios.');

      // Keep the header/sidebar name in sync with what was just saved.
      if (authUser) {
        dispatch(
          login({
            user: {
              ...authUser,
              firstName: updated.firstName,
              lastName: updated.lastName,
            },
            accessToken: getStoredTokens().idToken ?? '',
          }),
        );
      }
    } catch (err: any) {
      setError(err?.message || 'No pudimos guardar tus cambios.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Cargando tu perfil...</p>;

  const isTeacher = profile?.role === 'TEACHER';

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 4 }}>Mi perfil</h1>
      <p style={{ color: '#666', marginTop: 0 }}>Tu información de cuenta.</p>

      {error && (
        <div role="alert" style={alertStyle('#ffe9e9', '#cb4242', '#8f2323')}>
          {error}
        </div>
      )}
      {notice && <div style={alertStyle('#edf8e5', '#5ba617', '#3f7410')}>{notice}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label htmlFor="firstName" style={labelStyle}>
              Nombre
            </label>
            <input
              id="firstName"
              className="form-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
            />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label htmlFor="lastName" style={labelStyle}>
              Apellidos
            </label>
            <input
              id="lastName"
              className="form-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
            />
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <label htmlFor="timezone" style={labelStyle}>
            Zona horaria
          </label>
          <input
            id="timezone"
            className="form-input"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="America/Mexico_City"
          />
          <div className="form-help">Se usa para mostrar los horarios de tus sesiones.</div>
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          style={{ marginTop: 20 }}
          disabled={saving}
        >
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </form>

      <section style={{ marginTop: 32 }}>
        <h2 style={{ fontSize: 18, marginBottom: 10 }}>Datos de la cuenta</h2>
        <dl style={{ margin: 0 }}>
          <ReadOnlyRow label="Correo" value={profile?.email ?? '—'} />
          <ReadOnlyRow label="Tipo de cuenta" value={ROLE_LABEL[profile?.role ?? ''] ?? '—'} />
          <ReadOnlyRow label="Estado" value={profile?.status ?? '—'} />
        </dl>
        <p style={{ color: '#666', fontSize: 13, marginTop: 10 }}>
          Para cambiar tu correo o tu contraseña ve a <Link to="/settings">Configuración</Link>.
        </p>
      </section>

      {isTeacher && (
        <section style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 18, marginBottom: 6 }}>Perfil público de profesor</h2>
          <p style={{ color: '#666', fontSize: 14, marginTop: 0 }}>
            Tu biografía, materias, idiomas y certificaciones son parte de tu solicitud de profesor
            y son lo que ven los padres en el catálogo.
          </p>
          <Link
            to="/teacher-onboarding"
            className="btn btn-outline btn-sm"
            style={{ textDecoration: 'none' }}
          >
            Editar perfil de profesor
          </Link>
        </section>
      )}
    </div>
  );
};

const ReadOnlyRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 12,
      padding: '10px 0',
      borderBottom: '1px solid #e9ecef',
      flexWrap: 'wrap',
    }}
  >
    <dt style={{ color: '#666', fontSize: 14 }}>{label}</dt>
    <dd style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{value}</dd>
  </div>
);

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#444',
  marginBottom: 6,
};

function alertStyle(bg: string, border: string, fg: string): React.CSSProperties {
  return {
    background: bg,
    border: `1px solid ${border}`,
    color: fg,
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 16,
    fontSize: 14,
  };
}

export default Profile;
